/**
 * B632 (c) — the guest's own pages: sign in with a link (649:2) and the
 * orders bought with that email (649:111). Faces only; `meApp.tsx` holds the
 * calls. Built to the frames' ids; the sidecars are pulled beside them
 * (`spec/redline-guest-*.figma.json`).
 */
import type { ReactElement, ReactNode } from "react";
import { Banner, Button, DoorRow, EmptyState, Input, Skeleton, StageChip } from "@transact/ui";
import { clockWords, type MeCard, type MeOrderFace } from "../lib/me.js";

export { MeChangeSheet } from "./meChangeSheet.js";

/** 649:2 / 649:111 — the brand tile and name on the left, MY TICKETS (this page) on the right. */
function MeBar(props: { readonly businessName?: string | undefined }): ReactElement {
  return (
    <header className="me-bar">
      <p className="me-brand">
        <span className="me-brand-tile" aria-hidden="true" />
        {props.businessName ?? "Your tickets"}
      </p>
      <p className="me-here" aria-current="page">
        My tickets
      </p>
    </header>
  );
}

function MeShell(props: { readonly businessName?: string | undefined; readonly label: string; readonly wide?: boolean; readonly children: ReactNode }): ReactElement {
  return (
    <main className={props.wide === true ? "me me--wide" : "me"} aria-label={props.label}>
      <MeBar businessName={props.businessName} />
      <div className="me-body">{props.children}</div>
    </main>
  );
}

export type MeSignInPhase =
  | { readonly kind: "ask"; readonly refused?: boolean; readonly error?: string | undefined; readonly busy?: boolean; readonly failed?: boolean }
  | { readonly kind: "sent"; readonly to: string; readonly resendIn: number; readonly busy?: boolean };

/** 649:2 — no password: the email, one door, and the same answer whether or not the address has orders. */
export function MeSignIn(props: {
  readonly phase: MeSignInPhase;
  readonly email: string;
  readonly businessName?: string | undefined;
  readonly onEmail: (email: string) => void;
  readonly onSend: () => void;
  readonly onAnother: () => void;
}): ReactElement {
  const { phase } = props;
  return (
    <MeShell businessName={props.businessName} label="Sign in to your tickets">
      <h1 className="me-title">Your tickets, no password</h1>
      {phase.kind === "ask" ? (
        <>
          <p className="me-lede">Enter the email you bought with. We'll send a link that signs you in — it works once and expires in 15 minutes.</p>
          {phase.refused === true ? <Banner tone="warn">That sign-in link has already been used or has expired — ask for a fresh one below.</Banner> : null}
          {phase.failed === true ? <Banner tone="warn">We couldn't send the link just now. Nothing was sent — try again in a moment.</Banner> : null}
          {/* R14 (the B683 shape) — a form, so Enter in the field clicks the submit door; the press is the one send */}
          <form className="me-form" aria-label="Email me a sign-in link" onSubmit={(e) => e.preventDefault()}>
            <Input id="me-email" label="Email" type="email" placeholder="you@example.com" value={props.email} onChange={props.onEmail} error={phase.error} />
            <Button variant="primary" type="submit" busy={phase.busy === true} onClick={props.onSend}>
              Email me a sign-in link
            </Button>
          </form>
          <p className="me-foot">No account to make. Your confirmation email has a link that does the same thing.</p>
        </>
      ) : (
        <section className="me-sent" aria-labelledby="me-sent-title" role="status">
          <h2 className="me-sent-title" id="me-sent-title">
            Check your email
          </h2>
          <p className="me-sent-body" id="me-sent-body">
            {`We sent a sign-in link to ${phase.to}. Open it on this phone to see your tickets. Didn't get it? Check spam, or ${phase.resendIn > 0 ? `resend in ${clockWords(phase.resendIn)}` : "resend it now"}.`}
          </p>
          <div className="me-sent-doors">
            <Button variant="neutral" disabled={phase.resendIn > 0} describedBy="me-sent-body" busy={phase.busy === true} onClick={props.onSend}>
              Resend
            </Button>
            <Button variant="ghost" onClick={props.onAnother}>
              Use another email
            </Button>
          </div>
        </section>
      )}
    </MeShell>
  );
}

function OrderCard(props: {
  readonly card: MeCard;
  readonly live: boolean;
  readonly busy: boolean;
  readonly error?: string | undefined;
  readonly onTickets: () => void;
  readonly onManage?: (() => void) | undefined;
}): ReactElement {
  const { card } = props;
  return (
    <li className={`me-order ${props.live ? "me-order--live" : "me-order--past"}`} data-order={card.orderId}>
      <div className="me-order-head">
        <h3 className="me-order-show">{card.show}</h3>
        <StageChip tone={card.chip.tone}>{card.chip.word}</StageChip>
      </div>
      {props.live ? (
        <>
          {card.when !== undefined ? <p className="me-order-when">{card.when}</p> : null}
          <p className="me-order-what">{[card.what, card.code].filter((s) => s !== undefined && s !== "").join(" · ")}</p>
          {props.error !== undefined ? <Banner tone="warn">{props.error}</Banner> : null}
          <div className="me-order-doors">
            <Button variant="primary" busy={props.busy} onClick={props.onTickets}>
              Tickets
            </Button>
            {/* B632 (c) region 2 — 649:111's second door: the order's own page (650:2) */}
            {props.onManage !== undefined ? (
              <Button variant="neutral" onClick={props.onManage} ariaLabel={`Manage ${card.show}`}>
                Manage
              </Button>
            ) : null}
          </div>
        </>
      ) : (
        <>
          <p className="me-order-what">{[card.when, card.code].filter((s) => s !== undefined).join(" · ")}</p>
          <p className="me-order-what">{card.what}</p>
        </>
      )}
    </li>
  );
}

/** 649:111 — the orders bought with this email, what is coming up first. */
export function MeOrders(props: {
  readonly email: string;
  readonly businessName?: string | undefined;
  /** undefined = still reading. */
  readonly cards: { readonly upcoming: readonly MeCard[]; readonly past: readonly MeCard[] } | undefined;
  readonly error?: string | undefined;
  readonly ticketsBusy?: string | undefined;
  readonly ticketsError?: { readonly orderId: string; readonly message: string } | undefined;
  readonly onTickets: (orderId: string) => void;
  /** B632 (c) region 2 — MANAGE → the order's page. */
  readonly onManage?: ((orderId: string) => void) | undefined;
  readonly onSignOut: () => void;
  readonly onRetry?: (() => void) | undefined;
}): ReactElement {
  const { cards } = props;
  const onManage = props.onManage;
  return (
    <MeShell businessName={props.businessName} label="Your orders">
      <div className="me-who">
        <p className="me-email">{props.email}</p>
        <Button variant="textlink-accent" onClick={props.onSignOut}>
          Sign out
        </Button>
      </div>
      <h1 className="me-title">Your orders</h1>
      {/* B316 — the refusal stands above whatever the page already holds; it never replaces it */}
      {props.error !== undefined ? (
        <Banner tone="warn">
          {props.error}
          {props.onRetry !== undefined ? (
            <Button variant="textlink" onClick={props.onRetry}>
              Try again
            </Button>
          ) : null}
        </Banner>
      ) : null}
      {cards === undefined ? (
        props.error !== undefined ? null : (
        <div role="status" aria-label="Reading your orders">
          <Skeleton lines={6} />
        </div>
        )
      ) : cards.upcoming.length === 0 && cards.past.length === 0 ? (
        <EmptyState
          title={`No orders under ${props.email} yet`}
          body="Tickets bought with this email show here. Bought with another address? Sign out and use that one."
        />
      ) : (
        <>
          {cards.upcoming.length > 0 ? (
            <section className="me-section" aria-labelledby="me-upcoming">
              <h2 className="me-section-label" id="me-upcoming">
                Coming up
              </h2>
              <ul className="me-orders">
                {cards.upcoming.map((c) => (
                  <OrderCard
                    key={c.orderId}
                    card={c}
                    live
                    busy={props.ticketsBusy === c.orderId}
                    error={props.ticketsError?.orderId === c.orderId ? props.ticketsError.message : undefined}
                    onTickets={() => props.onTickets(c.orderId)}
                    onManage={onManage !== undefined ? () => onManage(c.orderId) : undefined}
                  />
                ))}
              </ul>
            </section>
          ) : null}
          {cards.past.length > 0 ? (
            <section className="me-section" aria-labelledby="me-past">
              <h2 className="me-section-label" id="me-past">
                Past
              </h2>
              <ul className="me-orders">
                {cards.past.map((c) => (
                  <OrderCard key={c.orderId} card={c} live={false} busy={false} onTickets={() => undefined} />
                ))}
              </ul>
            </section>
          ) : null}
        </>
      )}
      <p className="me-foot">Only orders bought with this email. Bought with another address? Sign in with that one — or ask the box office to merge them.</p>
    </MeShell>
  );
}

/* ---------------------------------------------------------------- 650:2 */

export type MeResendState = { readonly kind: "busy" } | { readonly kind: "sent" } | { readonly kind: "failed" };

/**
 * 650:2 @390 / 651:2 @1440 — one order, everything the buyer can use: the
 * tickets with their QR codes, what they can still do (the tenant's rule),
 * what they agreed to, and what they paid. One markup; the frame's two
 * columns at desktop widths are CSS (`.me-order-page` grid areas).
 */
export function MeOrderPage(props: {
  /** undefined = still reading (or the read failed — see `error`). */
  readonly face: MeOrderFace | undefined;
  readonly error?: string | undefined;
  /** token → its QR image, drawn as each loads. */
  readonly qr: Readonly<Record<string, string>>;
  readonly backHref: string;
  readonly businessName?: string | undefined;
  readonly resend?: MeResendState | undefined;
  readonly onPrint: () => void;
  readonly onResend: () => void;
  readonly onSignOut: () => void;
  readonly onRetry?: (() => void) | undefined;
  /** B632 (e) — "Change the …" opens the Change sheet; absent = the row says the rule and opens nothing. */
  readonly onChange?: (() => void) | undefined;
  /** B632 (e) — the night the order just moved to ("Sat, Oct 10 · 7:00 PM"). */
  readonly moved?: string | undefined;
}): ReactElement {
  const { face } = props;
  const back = (
    <p className="me-back">
      {/* dead-ok: the href navigates — Button draws a real <a> for it, the press needs no handler */}
      <Button variant="textlink-accent" href={props.backHref} onClick={() => undefined}>
        ‹ Your orders
      </Button>
    </p>
  );
  if (face === undefined)
    return (
      <MeShell businessName={props.businessName} label="Your order" wide>
        {back}
        {/* B316 — the refusal stands on its own line; the loading arm defers to it */}
        {props.error !== undefined ? (
          <Banner tone="warn">
            {props.error}
            {props.onRetry !== undefined ? (
              <Button variant="textlink" onClick={props.onRetry}>
                Try again
              </Button>
            ) : null}
          </Banner>
        ) : null}
        {props.error === undefined ? (
          <div role="status" aria-label="Reading your order">
            <Skeleton lines={8} />
          </div>
        ) : null}
      </MeShell>
    );
  const count = face.tickets.length;
  return (
    <MeShell businessName={props.businessName} label={`Your order — ${face.show}`} wide>
      <div className="me-order-page">
        <div className="me-op-head">
          {back}
          <div className="me-op-title-row">
            <h1 className="me-title me-op-title">{face.show}</h1>
            <StageChip tone={face.chip.tone}>{face.chip.word}</StageChip>
          </div>
          {face.when !== undefined ? <p className="me-op-when">{face.when}</p> : null}
          <p className="me-op-facts">{face.facts}</p>
        </div>
        <div className="me-op-doors">
          <Button variant="neutral" onClick={props.onPrint}>
            Print tickets
          </Button>
          {face.resend ? (
            <Button variant="neutral" busy={props.resend?.kind === "busy"} onClick={props.onResend}>
              Resend email
            </Button>
          ) : null}
        </div>
        {props.moved !== undefined ? (
          <div className="me-op-note">
            <Banner tone="success">{`Moved to ${props.moved}. Your new tickets are below and on their way to your email — the old ones no longer scan.`}</Banner>
          </div>
        ) : null}
        {props.resend?.kind === "sent" ? (
          <div className="me-op-note">
            <Banner tone="success">We sent your tickets to your email again.</Banner>
          </div>
        ) : props.resend?.kind === "failed" ? (
          <div className="me-op-note">
            <Banner tone="warn">We couldn't send them just now. Nothing was sent — try again in a moment.</Banner>
          </div>
        ) : null}
        <section className="me-panel me-op-tickets" aria-labelledby="me-op-tickets">
          <h2 className="me-section-label" id="me-op-tickets">{`Tickets · ${String(count)}`}</h2>
          {count === 0 ? (
            <p className="me-op-quiet">No tickets to show on this order — the box office can tell you why.</p>
          ) : (
            <ul className="me-tickets">
              {face.tickets.map((t, i) => {
                const src = t.token !== undefined ? props.qr[t.token] : undefined;
                return (
                  <li className="me-ticket" key={t.entitlementId}>
                    {src !== undefined ? (
                      <img className="me-ticket-qr" src={src} alt={`QR code for ${t.name}, ticket ${String(i + 1)} of ${String(count)}`} width={72} height={72} />
                    ) : (
                      <span className={`me-ticket-qr ${t.token !== undefined ? "me-ticket-qr--wait" : "me-ticket-qr--none"}`} aria-hidden="true" />
                    )}
                    <div className="me-ticket-words">
                      <p className="me-ticket-name">{t.name}</p>
                      <p className="me-ticket-ref">{t.ref}</p>
                      {t.status !== undefined ? <StageChip tone={t.token !== undefined ? "confirmed" : "void"}>{t.status}</StageChip> : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
        {face.canDo !== undefined ? (
          <section className="me-panel me-op-cando" aria-labelledby="me-op-cando">
            <h2 className="me-section-label" id="me-op-cando">
              What you can still do
            </h2>
            <ul className="me-cando">
              {face.canDo.map((r) => (
                <li key={r.key} className={`me-cando-row${r.offered ? "" : " me-cando-row--off"}`} data-cando={r.key}>
                  <DoorRow
                    title={r.title}
                    body={r.body}
                    off={!r.offered}
                    {...(r.key === "change" && r.offered && props.onChange !== undefined ? { onPress: props.onChange } : {})}
                  />
                </li>
              ))}
            </ul>
          </section>
        ) : null}
        <div className="me-op-side">
          {face.agreed !== undefined ? (
            <section className="me-panel" aria-labelledby="me-op-agreed">
              <h2 className="me-section-label" id="me-op-agreed">
                You agreed to
              </h2>
              <ul className="me-agreed">
                {face.agreed.map((a) => (
                  <li key={a}>{a}</li>
                ))}
              </ul>
            </section>
          ) : null}
          <section className="me-panel" aria-labelledby="me-op-paid">
            <h2 className="me-section-label" id="me-op-paid">
              {face.paidOn !== undefined ? `Paid · ${face.paidOn}` : "Paid"}
            </h2>
            <dl className="me-paid">
              {face.paid.map((r) => (
                <div className="me-paid-row" key={r.label}>
                  <dt>{r.label}</dt>
                  <dd>{r.amount}</dd>
                </div>
              ))}
              <div className="me-paid-row me-paid-row--total">
                <dt>{face.total.label}</dt>
                <dd>{face.total.amount}</dd>
              </div>
            </dl>
          </section>
          <section className="me-panel" aria-labelledby="me-op-help">
            <h2 className="me-section-label" id="me-op-help">
              Need something else?
            </h2>
            <p className="me-op-quiet">Reply to your confirmation email — it reaches the box office. Bring your order code.</p>
          </section>
        </div>
        <div className="me-who me-op-who">
          <Button variant="textlink-accent" onClick={props.onSignOut}>
            Sign out
          </Button>
        </div>
      </div>
    </MeShell>
  );
}
